function stripTrailingSlashes(raw) {
  const s = (raw || '').toString().trim();
  if (!s) return '';
  return s.replace(/\/+$/, '');
}

/**
 * Base URL for absolute public links (share, QR, meta). Prefer `REACT_APP_SITE_URL` at build time
 * so admins generating assets on localhost still encode production URLs. See docs/PUBLIC_HOSTING_AND_ROUTING.md.
 */
export function getPublicAppBaseUrl() {
  const envBase = stripTrailingSlashes(process.env.REACT_APP_SITE_URL);
  if (typeof window !== 'undefined') {
    const origin = stripTrailingSlashes(window.location.origin);
    return envBase || origin || '';
  }
  return envBase;
}

/**
 * Public memory book URLs (works for social / messaging). Crawlers that do not run JS
 * still need server-side or prerendered meta for rich previews.
 */
export function buildPublicMemoryBookUrl({ year, page = 'main', teamId } = {}) {
  const base = getPublicAppBaseUrl();
  if (!base || !year) return '';
  const y = Number.parseInt(String(year), 10);
  if (!Number.isFinite(y)) return '';

  const path =
    page === 'leaderboard'
      ? `/${y}/archive/leaderboard`
      : page === 'quotes'
        ? `/${y}/archive/quotes`
        : page === 'gallery'
          ? `/${y}/archive/gallery`
          : page === 'videos'
            ? `/${y}/archive/videos`
          : `/${y}/archive`;

  const q = new URLSearchParams();
  if (teamId) q.set('teamId', teamId);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return `${base}${path}${suffix}`;
}

/**
 * Live public leaderboard for an edition (use when the memory book is not published yet).
 */
export function buildPublicLeaderboardUrl({ year, eventId } = {}) {
  const base = getPublicAppBaseUrl();
  if (!base || !year) return '';
  const y = Number.parseInt(String(year), 10);
  if (!Number.isFinite(y)) return '';
  const q = new URLSearchParams();
  if (eventId) q.set('event', String(eventId));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return `${base}/${y}/leaderboard${suffix}`;
}

/**
 * Prefer Web Share API (mobile); fall back to clipboard; return outcome for UI feedback.
 */
export async function shareOrCopyLink({ title, text, url }) {
  const payload = { title: title || '', text: text || title || '', url: url || '' };
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share(payload);
      return 'shared';
    } catch (e) {
      if (e && e.name === 'AbortError') return 'aborted';
    }
  }
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(url);
      return 'copied';
    } catch {
      /* fall through */
    }
  }
  return 'failed';
}
